import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";

// Use a relative URL when in production, allowing the Nginx proxy to handle routing
const API_URL =
  process.env.NODE_ENV === "production"
    ? "/api"
    : process.env.REACT_APP_API_URL || "http://localhost:8080/api";

const PaymentSuccess = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [apiKey, setApiKey] = useState(null);
  const [sessionInfo, setSessionInfo] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const verifyPayment = async () => {
      // Only verify payment if not already processed
      if (localStorage.getItem(`paymentProcessed_${location.search}`)) {
        const savedData = JSON.parse(
          localStorage.getItem(`paymentProcessed_${location.search}`)
        );
        setSessionInfo(savedData.sessionInfo);
        setApiKey(savedData.apiKey);
        setLoading(false);
        return;
      }

      try {
        // Extract the session ID from the URL query parameters
        const queryParams = new URLSearchParams(location.search);
        const sessionId = queryParams.get("sid");

        if (!sessionId) {
          setError("No session ID found in the URL");
          setLoading(false);
          return;
        }

        // Verify the payment with the backend
        const response = await axios.get(
          `${API_URL}/verify-payment/${sessionId}`
        );

        // Check if payment was actually completed
        if (!response.data.completed) {
          setError(
            "Payment verification failed: This session has not been completed"
          );
          setLoading(false);
          return;
        }

        setSessionInfo(response.data);
        setApiKey(response.data.apiKey);

        // Store the API key if one was returned
        if (response.data.apiKey) {
          localStorage.setItem("apiKey", response.data.apiKey);

          // Store the email if one is returned
          if (response.data.email) {
            localStorage.setItem("userEmail", response.data.email);
          }

          // Reload the main app to pick up the new API key
          window.opener?.postMessage(
            { type: "API_KEY_UPDATED", apiKey: response.data.apiKey },
            window.location.origin
          );
        }

        // Mark this payment as processed so we don't re-verify on refresh
        localStorage.setItem(
          `paymentProcessed_${location.search}`,
          JSON.stringify({
            sessionInfo: response.data,
            apiKey: response.data.apiKey,
          })
        );

        setLoading(false);
      } catch (err) {
        setError(
          "We couldn't verify your payment. Please contact support if you believe this is an error."
        );
        setLoading(false);
      }
    };

    verifyPayment();
  }, [location]);

  const copyApiKey = () => {
    navigator.clipboard.writeText(apiKey);
    alert("API key copied to clipboard!");
  };

  const handleGoToDashboard = () => {
    // If this window was opened by another window, close it after updating
    if (window.opener) {
      window.opener.postMessage(
        { type: "NAVIGATE_TO", path: "/dashboard" },
        window.location.origin
      );
      window.close();
    } else {
      // Otherwise just navigate normally
      navigate("/dashboard");
    }
  };

  const handleGoHome = () => {
    // If this window was opened by another window, close it after updating
    if (window.opener) {
      window.opener.postMessage(
        { type: "NAVIGATE_TO", path: "/" },
        window.location.origin
      );
      window.close();
    } else {
      // Otherwise just navigate normally
      navigate("/");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh] p-8">
        <div className="card max-w-xl w-full text-center border-t-4 border-blue-500">
          <h2 className="text-2xl font-bold mb-4 text-gray-800 dark:text-white">
            Verifying your payment...
          </h2>
          <div className="inline-block w-12 h-12 border-4 border-gray-200 dark:border-gray-700 border-t-blue-500 dark:border-t-blue-400 rounded-full animate-spin my-6"></div>
          <p className="text-gray-600 dark:text-gray-300">
            This may take a moment. Please don't close this page.
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-[60vh] p-8">
        <div className="card max-w-xl w-full text-center border-t-4 border-red-500">
          <h2 className="text-2xl font-bold mb-4 text-gray-800 dark:text-white">
            Payment Verification Failed
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-8">{error}</p>
          <div className="flex gap-4 justify-center">
            <button
              onClick={() => navigate("/buy-credits")}
              className="btn btn-primary"
            >
              Try Again
            </button>
            <button onClick={handleGoHome} className="btn btn-secondary">
              Go Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-center items-center min-h-[60vh] p-8">
      <div className="card max-w-xl w-full text-center border-t-4 border-green-500">
        <div className="inline-flex justify-center items-center w-20 h-20 bg-green-500 text-white text-4xl rounded-full mb-6">
          ✓
        </div>
        <h2 className="text-2xl font-bold mb-4 text-gray-800 dark:text-white">
          Payment Successful!
        </h2>

        {sessionInfo && (
          <div className="bg-gray-100 dark:bg-gray-700 p-4 rounded-md my-6">
            <p className="text-gray-700 dark:text-gray-300">
              You've successfully purchased{" "}
              <strong>{sessionInfo.credits} credits</strong>.
            </p>
            {sessionInfo.isNewKey && (
              <p className="text-gray-700 dark:text-gray-300 mt-2">
                Your new API key has been generated and is ready to use.
              </p>
            )}
          </div>
        )}

        {apiKey && (
          <div className="my-6">
            <h3 className="text-xl font-semibold mb-4 text-gray-800 dark:text-white">
              Your API Key
            </h3>
            <div className="bg-gray-100 dark:bg-gray-700 p-4 rounded-md font-mono break-all mb-4 flex items-center justify-between">
              <code className="text-sm">{apiKey}</code>
              <button
                className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm ml-4"
                onClick={copyApiKey}
              >
                Copy
              </button>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Store this key securely. You'll need it to access our API.
            </p>
          </div>
        )}

        <div className="flex gap-4 justify-center mt-8">
          <button onClick={handleGoToDashboard} className="btn btn-primary">
            Go to Dashboard
          </button>
          <button onClick={handleGoHome} className="btn btn-secondary">
            Go Home
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
